import React from 'react'
import { Orb, OrbStateBadge, type AgentState } from '@/uiV2/elevenLabs/orb'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import Button from '../../common/form/inputs/Button'

type AgentPresetOption = {
    label: string
    agentId: string
}

type Props = {
    error: string | null
    agentId: string
    onAgentIdChange: (nextAgentId: string) => void
    agentPresetId: string
    onAgentPresetChange: (nextAgentId: string) => void
    agentPresetOptions: AgentPresetOption[]
    isAgentSelectionLocked: boolean
    isCheckingImportStatus: boolean
    isPreparingPerson: boolean
    status: string
    canStart: boolean
    isStarting: boolean
    startMode: 'real' | 'mock' | null
    onStart: () => void
    canStartMock: boolean
    onStartMock: () => void
    callMode: 'real' | 'mock' | null
    agentState: AgentState
    getOutputVolume: () => number
    showHangUp: boolean
    canHangUp: boolean
    isStopping: boolean
    onHangUp: () => void
}

export default function StartConversationCard({
    error,
    agentId,
    onAgentIdChange,
    agentPresetId,
    onAgentPresetChange,
    agentPresetOptions,
    isAgentSelectionLocked,
    isCheckingImportStatus,
    isPreparingPerson,
    status,
    canStart,
    isStarting,
    startMode,
    onStart,
    canStartMock,
    onStartMock,
    callMode,
    agentState,
    getOutputVolume,
    showHangUp,
    canHangUp,
    isStopping,
    onHangUp,
}: Props): React.JSX.Element {
    const isStartingReal = isStarting && startMode === 'real'
    const isStartingMock = isStarting && startMode === 'mock'
    const hangUpLabel =
        isStopping || status === 'disconnecting'
            ? 'Ending...'
            : callMode === 'mock'
              ? 'End call'
              : 'Hang up'

    return (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] space-y-4">
            <div>
                <h2 className="text-lg font-semibold text-foreground">
                    Start the Kit conversation
                </h2>
                <p className="text-sm text-muted-foreground">
                    Real and mock calls can run without a selected profile. If
                    no profile is selected, we send fallback profile data to the
                    agent.
                </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <div>
                    <label
                        htmlFor="kit-test-agent-preset"
                        className="block text-sm font-semibold text-foreground"
                    >
                        Agent preset
                    </label>
                    <select
                        id="kit-test-agent-preset"
                        className="form-input mt-1 w-full"
                        value={agentPresetId}
                        onChange={(event) =>
                            onAgentPresetChange(event.target.value)
                        }
                        disabled={isAgentSelectionLocked}
                    >
                        {agentPresetOptions.map((preset) => (
                            <option key={preset.agentId} value={preset.agentId}>
                                {preset.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label
                        htmlFor="kit-test-agent-id"
                        className="block text-sm font-semibold text-foreground"
                    >
                        Agent id
                    </label>
                    <input
                        id="kit-test-agent-id"
                        className="form-input mt-1 w-full"
                        placeholder="agent_..."
                        value={agentId}
                        onChange={(event) =>
                            onAgentIdChange(event.target.value)
                        }
                        disabled={isAgentSelectionLocked}
                    />
                </div>
            </div>
            <p className="text-xs text-muted-foreground">
                Pick a preset to auto-fill the agent id, or edit it directly.
            </p>

            {error ? <Note type={NoteType.ERROR}>{error}</Note> : null}

            {isCheckingImportStatus ? (
                <Note type={NoteType.INFO}>
                    Checking network import status...
                </Note>
            ) : null}

            {isPreparingPerson ? (
                <Note type={NoteType.INFO}>
                    Importing the profile into the graph...
                </Note>
            ) : null}

            {status === 'disconnected' ? (
                <div className="space-y-3">
                    <Button
                        type="button"
                        style="primary"
                        enabled={canStart}
                        classes="w-full justify-center gap-2 py-3 text-base shadow-lg shadow-ctxt-blue/20 hover:shadow-xl transition-all"
                        onClick={onStart}
                    >
                        {isStartingReal ? 'Preparing...' : 'Start Kit Test'}
                    </Button>
                    <Button
                        type="button"
                        style="secondary"
                        enabled={canStartMock}
                        classes="w-full justify-center gap-2 py-3 text-base"
                        onClick={onStartMock}
                    >
                        {isStartingMock
                            ? 'Starting mock call...'
                            : 'Start Mock Call'}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                        Mock calls skip ElevenLabs and only hit backend
                        endpoints.
                    </p>
                </div>
            ) : null}

            {status !== 'disconnected' ? (
                <div className="flex w-full flex-col items-center gap-4">
                    {callMode === 'mock' ? (
                        <p className="text-xs text-muted-foreground">
                            Mock call active. ElevenLabs audio is disabled.
                        </p>
                    ) : null}
                    <div className="w-full rounded-2xl border border-border bg-secondary/50 p-4">
                        <div className="mx-auto aspect-square max-w-48">
                            {status === 'connected' ? (
                                <Orb
                                    className="h-full w-full"
                                    agentState={agentState}
                                    volumeMode="manual"
                                    getOutputVolume={getOutputVolume}
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                                    Connecting to Kit...
                                </div>
                            )}
                        </div>
                    </div>
                    <OrbStateBadge agentState={agentState} />
                    {showHangUp ? (
                        <Button
                            type="button"
                            style="mild-danger"
                            enabled={canHangUp}
                            classes="min-w-[140px]"
                            onClick={onHangUp}
                        >
                            {hangUpLabel}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}
